import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { useUserDetails } from "../Dashboard/UserDetailsContext";
import PageHeader from "../PageHeader/PageHeader";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, CircularProgress } from "@mui/material";
import { useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer, toast } from "react-toastify";
import { useExpenseStore } from "../store/useExpenseStore";

const getTodayDate = () => new Date().toISOString().split("T")[0];

const baseURL =
  "https://s3.ap-southeast-1.amazonaws.com/attachments.shoptalk.in/prod/";

const formatDateForInput = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function ExpenseForm({ expense, onSubmit, onCancel }) {
  const store = useExpenseStore();
  const { userDetails } = useUserDetails();
  const navigate = useNavigate();
  const location = useLocation();
  const [inputValue, setInputValue] = useState("");

  const [formData, setFormData] = useState({
    amount: "",
    category: "",
    description: "",
    receipt: null,
    expenseDate: getTodayDate(),
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingReceiptUrl, setExistingReceiptUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false); // <-- Add this

  const urlParams = new URLSearchParams(location.search);

  const token = urlParams.get("token") || urlParams.get("deviceToken");
  const buildPOUrl = (path: string) => (token ? `${path}?token=${token}` : path);

  const uploadFileToS3 = async (file, teamContactID) => {
    // Prevent invalid file uploads
    if (!file || !file.name) {
      setError("Please select a file before uploading.");
      return null;
    }
    try {
      const newFileName = `${teamContactID}_${file.name}`;
      const presignRes = await axios.post(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_PORTNO}/Messages/GeneratePreSignedURL`,
        {
          FileName: newFileName,
          FileType: file.type,
        }
      );

      const { url } = presignRes.data;
      console.log("nani ganesh",url)
      if (!url) throw new Error("Missing presigned URL.");

      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      return `${process.env.REACT_APP_ATTACHMENT_URL}${newFileName}`;
    } catch (err) {
      console.error("Upload error:", err);
      setError("Failed to upload receipt.");
      return null;
    }
  };

  useEffect(() => {
    if (expense) {
      setFormData((prev) => ({
        ...prev,
        amount: expense.Amount || "",
        // Only set if category exists in categories, else fallback to ""
        category:
          categories.some((cat) => cat.Category === expense.CategoryName) ||
          expense.CategoryName === ""
            ? expense.CategoryName
            : "",
        description: expense.Description || "",
        receipt: expense.ReceiptURL || null,
        expenseDate: expense.ExpenseDate
          ? formatDateForInput(expense.ExpenseDate)
          : getTodayDate(),
      }));

      setExistingReceiptUrl(expense.ReceiptURL || null); // <-- Track existing URL
    }
    // eslint-disable-next-line
  }, [expense, categories]); // depend on categories so category option is always valid

  const fetchCategories = async () => {
    if (!userDetails?.BusinessID) return;
    setLoading(true);
    setError(null);
    try {
      await store.loadCategoriesByBusiness(userDetails.BusinessID);
      const list = useExpenseStore.getState().getCategories(userDetails.BusinessID);
      setCategories(list);
      if (!Array.isArray(list) || list.length === 0) {
        setError("No categories found.");
      }
    } catch (err) {
      setError("Error fetching categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [userDetails]);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true); // <-- Set submitting true

    const selectedCategory = categories.find(
      (cat) => cat.Category === formData.category
    );
    if (!selectedCategory) {
      setError("Selected category not found.");
      setSubmitting(false);
      return;
    }

    let receiptUrl = existingReceiptUrl || "null";
    // Only upload if formData.receipt is a File, not a string (URL)
    if (
      formData.receipt &&
      typeof formData.receipt !== "string" &&
      formData.receipt instanceof File
    ) {
      receiptUrl = await uploadFileToS3(
        formData.receipt,
        userDetails.TeamContactID
      );
      if (!receiptUrl) {
        setSubmitting(false);
        return;
      }
    }

    const baseData = {
      TeamContactID: String(userDetails?.TeamContactID),
      CategoryID: String(selectedCategory.ID),
      Amount: String(formData.amount),
      Description: formData.description,
      ReceiptURL: receiptUrl,
      ExpenseDate: formData.expenseDate || getTodayDate(),
    };

    const payload = expense
      ? { ...baseData, ExpenseID: String(expense.ExpenseID) }
      : baseData;

    try {
      if (!expense) {
        await store.addExpense(payload);
        await store.loadDashboardSummaries({
          BusinessID: userDetails?.BusinessID,
          TeamContactID: userDetails?.TeamContactID,
          UserRole: userDetails?.UserRole,
        }, { force: true });

        // Fetch business expenses after adding
        const businessId = userDetails?.BusinessID;
        if (businessId) {
          await store.loadExpensesByBusiness(businessId, { force: true });
        }
      } else {
        await store.updateExpense(payload);

        // Fetch business expenses after editing
        const businessId = userDetails?.BusinessID;
        if (businessId) {
          await store.loadExpensesByBusiness(businessId, { force: true });
        }
      }

      toast.dismiss("expense-submit-progress");
      toast.success(`Expense ${expense ? "updated" : "added"} successfully!`, {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      onSubmit && onSubmit(payload);

      setTimeout(() => {
        navigate(buildPOUrl(`/`));
      }, 2000);
    } catch (err) {
      toast.dismiss("expense-submit-progress");
      console.error("Submit error:", err);
      setError("Error submitting expense.");
    } finally {
      setSubmitting(false); // <-- Reset submitting
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, receipt: file });
  };
  const handleAddCategory = async (newCategory) => {
    if (!newCategory || !userDetails?.BusinessID) {
      setError("Please enter a category name and ensure BusinessID is available.");
      return;
    }

    try {
      await store.addCategory({ CategoryName: newCategory, BusinessID: userDetails.BusinessID });
      await fetchCategories();
      setFormData((prevFormData) => ({ ...prevFormData, category: newCategory }));
      setInputValue(newCategory);
      setError(null);
      
    } catch (error) {
      console.error("Error adding category:", error);
      setError("Error adding category.");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ p: 2 }}
      className="custom-select"
    >
      <div>
        {expense ? (
          <Box mt={1} mb={2}>
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: "normal" }}
            >
              Edit Expense
            </Typography>
          </Box>
        ) : (
          <PageHeader data="Add Expense" />
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            sx={{
              textTransform: "none", // Prevents automatic capitalization
              fontSize: "0.875rem", // Optional: Increase or change font size here
            }}
            size="small"
            style={{ whiteSpace: "nowrap" }}
            onClick={() =>
              navigate(
                buildPOUrl(
                  `/manage-categories`
                )
              )
            }
            className="bg-primary"
          >
            Manage Categories
          </Button>
        </div>
      </div>

      <TextField
        fullWidth
        type="number"
        label="Amount"
        margin="normal"
        value={formData.amount}
        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
        required
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "var(--text-primary)",
            },
            "&:hover fieldset": {
              borderColor: "var(--text-primary)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "var(--text-primary)",
            },
          },
          "& .MuiInputLabel-root": {
            color: "var(--text-primary)",
          },
          "& .MuiInputBase-input": {
            color: "var(--text-primary)",
          },
        }}
      />

      {/* <FormControl fullWidth margin="normal">
        <InputLabel
          id="category-label"
          sx={{ color: "var(--text-primary)" }}
        >
          Category
        </InputLabel>
        <Select
          labelId="category-label"
          value={
            categories.length > 0 &&
            categories.some((cat) => cat.Category === formData.category)
              ? formData.category
              : ""
          }
          label="Category"
          onChange={(e) =>
            setFormData({ ...formData, category: e.target.value })
          }
          required
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--text-primary)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--text-primary)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--text-primary)",
            },
            "& .MuiSelect-select": {
              color: "var(--text-primary)",
            },
          }}
        >
          <MenuItem value="">
            <em>Select a category</em>
          </MenuItem>
          {loading ? (
            <MenuItem disabled>Loading categories...</MenuItem>
          ) : error ? (
            <MenuItem disabled>{error}</MenuItem>
          ) : (
            categories.map((category) => (
              <MenuItem key={category.ID} value={category.Category}>
                {category.Category}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl> */}

      <FormControl fullWidth margin="normal">
        <Autocomplete
          freeSolo
          options={categories}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            return option.Category || "";
          }}
          value={formData.category}
          onChange={(event, newValue) => {
            if (typeof newValue === "string") {
              handleAddCategory(newValue);
            } else if (newValue && newValue.inputValue) {
              handleAddCategory(newValue.inputValue);
            } else {
              setFormData({ ...formData, category: newValue ? newValue.Category : "" });
            }
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
          }}
          loading={loading}
          renderOption={(props, option) => {
            if (typeof option === "string") {
              return <li {...props}>{option}</li>;
            }
            return <li {...props}>{option.Category}</li>;
          }}
          filterOptions={(options, params) => {
            const filtered = options.filter((option) =>
              option.Category.toLowerCase().includes(params.inputValue.toLowerCase())
            );

            if (
              params.inputValue !== "" &&
              !filtered.some(
                (option) => option.Category.toLowerCase() === params.inputValue.toLowerCase()
              )
            ) {
              filtered.push({
                inputValue: params.inputValue,
                Category: `Add "${params.inputValue}"`,
              });
            }

            return filtered;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              required
              error={!!error}
              helperText={error}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Description"
        margin="normal"
        value={formData.description}
        onChange={(e) =>
          setFormData({ ...formData, description: e.target.value })
        }
        required
      />

      <TextField
        fullWidth
        type="date"
        label="Expense Date"
        margin="normal"
        value={formData.expenseDate}
        onChange={(e) =>
          setFormData({ ...formData, expenseDate: e.target.value })
        }
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: getTodayDate() }}
        required
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "var(--text-primary)",
            },
            "&:hover fieldset": {
              borderColor: "var(--text-primary)",
            },
            "&.Mui-focused fieldset": {
              borderColor: "var(--text-primary)",
            },
          },
          "& .MuiInputLabel-root": {
            color: "var(--text-primary)",
          },
          "& .MuiInputBase-input": {
            color: "var(--text-primary)",
          },
        }}
      />

      <Button
        variant="contained"
        component="label"
        sx={{
          mt: 2,
          mr: 2,
          textTransform: "none", // Prevents automatic capitalization
          // Optional: Increase or change font size here
        }}
        className="bg-primary"
      >
        Upload Receipt
        <input
          type="file"
          hidden
          accept="image/*,application/pdf"
          onChange={handleFileChange}
        />
      </Button>

      {formData.receipt && formData.receipt !== "null" && (
        <Box
          mt={1}
          position="relative"
          display="flex"
          alignItems="center"
          sx={{ maxWidth: "100%", paddingLeft: "32px" }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            <Button
              size="small"
              onClick={() => setFormData({ ...formData, receipt: null })}
              sx={{
                minWidth: "24px",
                minHeight: "24px",
                padding: "2px",
                backgroundColor: "white",
                border: "1px solid lightgray",
                borderRadius: "50%",
                lineHeight: 1,
                "&:hover": {
                  backgroundColor: "#f8d7da",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 12 }} />
            </Button>
          </Box>
          <Box sx={{ mt: 1 }}>
            <strong>Selected file:</strong>{" "}
            {typeof formData.receipt === "string"
              ? formData.receipt.split("/").pop()
              : formData.receipt.name}
          </Box>
        </Box>
      )}
      <Button
        type="submit"
        variant="contained"
        sx={{ mt: 2, mr: 2, textTransform: "none", color: "white" }}
        className="bg-primary"
       
        disabled={submitting}
        
      >
        {submitting
          ? expense
            ? "Updating..."
            : "Submitting..."
          : expense
          ? "Update"
          : "Submit"}
      </Button>

      {onCancel && (
        <Button
          variant="contained"
          onClick={onCancel}
          sx={{ mt: 2, textTransform: "none" }}
          className="bg-primary"
          disabled={submitting}
        >
          Cancel
        </Button>
      )

      }
      {error && (
        <Box mt={2} color="error.main">
          {error}
        </Box>
      )}
      <ToastContainer />
    </Box>
  );
}